/**
 * Auth session limits — decisions.md §2 (N16, owner 2026-10-15: "people need to be kept logged
 * in"; lessons run 2 h+).
 *
 * - Idle: signed out after 8 hours with no activity (input, video playback, any API request).
 *   Env `SESSION_IDLE_TIMEOUT_MINUTES`.
 * - Lifetime: a session lives 30 days from its last activity — sliding, so a regular student is
 *   never forced to sign in again while they keep using the app. Env `SESSION_MAX_AGE_DAYS`.
 *
 * - Absolute cap (SEC-5, post-remediation sweep): however active, a session ends this long after
 *   its sign-in. Generous on purpose; never shorter than the lifetime. Env
 *   `SESSION_ABSOLUTE_CAP_DAYS` (default 90).
 * - Refresh tokens rotate on every refresh. The previous one still works while the newer one was
 *   never used (lost response) and within `REFRESH_REUSE_GRACE_SECONDS` (default 60) of the
 *   rotation (two tabs); otherwise a superseded token revokes the session (rotateRefreshGeneration).
 *
 * Revocation is unaffected: logout, password reset, removal or a role change still end a session
 * on the next request (SEC-5).
 *
 * This module is imported by the SPA too (the compiled-in default idle limit), so it reads
 * `process.env` only inside functions the server calls.
 */
export const SESSION_IDLE_MAX_AGE_SEC = 8 * 60 * 60;
/** Default session lifetime; sliding — renewed by activity (see `touchAuthSession`). */
export const SESSION_ABSOLUTE_MAX_AGE_SEC = 30 * 24 * 60 * 60;
export const SESSION_IDLE_COOKIE = "lms_session_idle";
/** SEC-5 (sweep): default absolute cap from sign-in, however active the session is. */
export const SESSION_ABSOLUTE_CAP_DEFAULT_SEC = 90 * 24 * 60 * 60;
/** SEC-5 (sweep): default grace for the previous refresh generation after a rotation. */
export const REFRESH_REUSE_GRACE_DEFAULT_SEC = 60;

/** Below this an env value is treated as a typo (it would sign everyone out constantly). */
const MIN_IDLE_MINUTES = 5;

function wholeNumber(raw: string | undefined): number | null {
  if (!raw || !/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw.trim());
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/** Session lifetime in seconds (sliding). */
export function sessionMaxAgeSec(env: NodeJS.ProcessEnv = process.env): number {
  const days = wholeNumber(env.SESSION_MAX_AGE_DAYS);
  return days ? days * 24 * 60 * 60 : SESSION_ABSOLUTE_MAX_AGE_SEC;
}

/** Idle sign-out in seconds; never longer than the session lifetime. */
export function sessionIdleMaxAgeSec(env: NodeJS.ProcessEnv = process.env): number {
  const minutes = wholeNumber(env.SESSION_IDLE_TIMEOUT_MINUTES);
  const idle = minutes && minutes >= MIN_IDLE_MINUTES ? minutes * 60 : SESSION_IDLE_MAX_AGE_SEC;
  return Math.min(idle, sessionMaxAgeSec(env));
}

/** SEC-5 (sweep): absolute cap in seconds from sign-in; never below the session lifetime. */
export function sessionAbsoluteCapSec(env: NodeJS.ProcessEnv = process.env): number {
  const days = wholeNumber(env.SESSION_ABSOLUTE_CAP_DAYS);
  const cap = days ? days * 24 * 60 * 60 : SESSION_ABSOLUTE_CAP_DEFAULT_SEC;
  return Math.max(cap, sessionMaxAgeSec(env));
}

/** SEC-5 (sweep): seconds the previous refresh generation is still honoured after a rotation. */
export function refreshReuseGraceSec(env: NodeJS.ProcessEnv = process.env): number {
  return wholeNumber(env.REFRESH_REUSE_GRACE_SECONDS) ?? REFRESH_REUSE_GRACE_DEFAULT_SEC;
}
